// DEPLOY: /var/www/woody.wtf/frontend/src/chart/DrawingBar.js
// Floating bar shown while one or more drawings are selected: colour, line width, line style,
// line / fill opacity sliders, price labels on/off, edit text, separate callout outline/text/fill colours, settings, lock, delete.
// With several selected, a control shows as active only when they all share that value,
// and changes apply to all of them. Desktop: top centre of the chart. Phone: above the
// bottom toolbar, wrapping onto two rows.
import React from 'react';
import { IconLock, IconTrash, IconText, IconFib, IconTag, IconCandles } from './icons';
import { HAS_FILL } from './drawings';

const COLOURS = ['#7C3AED', '#2563EB', '#16A34A', '#DC2626', '#F59E0B', '#111111'];

const S = {
  bar:    { position: 'absolute', left: '50%', transform: 'translateX(-50%)', zIndex: 15, display: 'flex', alignItems: 'center', gap: '4px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)', padding: '4px 6px', maxWidth: 'calc(100% - 16px)', overflowX: 'auto' },
  // Phone: full width, wraps onto two rows so lock / delete are never off-screen.
  mobile: { bottom: '8px', left: '8px', right: '8px', transform: 'none', maxWidth: 'none', flexWrap: 'wrap', justifyContent: 'center', overflowX: 'visible' },
  swatch: { width: '20px', height: '20px', borderRadius: '50%', border: '2px solid var(--surface)', cursor: 'pointer', flexShrink: 0, padding: 0 },
  sep:    { width: '1px', height: '20px', background: 'var(--border)', margin: '0 3px', flexShrink: 0 },
  btn:    { minWidth: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', borderRadius: '6px', background: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '12px', fontFamily: "'DM Mono',monospace", flexShrink: 0 },
  slider: { display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontFamily: "'DM Mono',monospace", color: 'var(--text-muted)', flexShrink: 0 },
  range:  { width: '70px', accentColor: 'var(--accent)' },
  count:  { fontSize: '11px', fontFamily: "'DM Mono',monospace", color: 'var(--text-muted)', padding: '0 4px', flexShrink: 0 },
};

const Line = ({ width = 2, dash = [] }) => (
  <svg width="22" height="12"><line x1="2" y1="6" x2="20" y2="6" stroke="currentColor" strokeWidth={width} strokeDasharray={dash.join(' ')} /></svg>
);

function Slider({ label, value, onChange }) {
  const pct = Math.round(value * 100);
  return (
    <label style={S.slider} title={`${label} opacity`}>
      {label}
      <input type="range" min="0" max="100" value={pct} style={S.range} onChange={e => onChange(Number(e.target.value) / 100)} />
      <span style={{ width: '30px' }}>{pct}%</span>
    </label>
  );
}

export default function DrawingBar({ items, mobile, onChange, onDelete, onEditText, onSettings }) {
  if (!items?.length) return null;
  const same = f => items.every(d => f(d) === f(items[0]));
  const st = k => (same(d => d.style[k]) ? items[0].style[k] : undefined);
  const on = active => (active ? { background: 'var(--accent-dim)', color: 'var(--accent)' } : {});
  const setStyle = patch => onChange({ style: patch });
  const one = items.length === 1 ? items[0] : null;
  const anyFill = items.some(d => HAS_FILL.has(d.type));
  const allLocked = items.every(d => d.locked);
  const allPriceLabels = items.every(d => ['hline', 'hray', 'fib'].includes(d.type));
  const tagsOn = items.every(d => d.style.priceTag !== false);
  const allMeasure = items.every(d => d.type === 'measure');
  const allChannel = items.every(d => d.type === 'channel');
  const callout = one && ['callout', 'bubble'].includes(one.type);
  const css = name => getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  const colour = (value, fallback) => /^#[0-9a-f]{6}$/i.test(value) ? value : fallback;

  return (
    <div style={{ ...S.bar, ...(mobile ? S.mobile : { top: '34px' }) }} onPointerDown={e => e.stopPropagation()}>
      {items.length > 1 && <span style={S.count}>{items.length} selected</span>}
      {!callout && (
        <>
          {COLOURS.map(c => (
            <button key={c} title="Colour" onClick={() => setStyle({ color: c })}
                    style={{ ...S.swatch, background: c, boxShadow: st('color') === c ? `0 0 0 2px ${c}` : 'none' }} />
          ))}
          <span style={S.sep} />
        </>
      )}
      {[1, 2, 3].map(w => (
        <button key={w} title={`Width ${w}`} style={{ ...S.btn, ...on(st('width') === w) }} onClick={() => setStyle({ width: w })}><Line width={w} /></button>
      ))}
      <span style={S.sep} />
      {[['solid', []], ['dash', [5, 3]], ['dot', [1.5, 3]]].map(([k, dash]) => (
        <button key={k} title={k} style={{ ...S.btn, ...on(st('dash') === k) }} onClick={() => setStyle({ dash: k })}><Line dash={dash} /></button>
      ))}
      <span style={S.sep} />
      <Slider label="Line" value={items[0].style.lineOpacity ?? 1} onChange={v => setStyle({ lineOpacity: v })} />
      {anyFill && <Slider label="Fill" value={items.find(d => HAS_FILL.has(d.type)).style.fillOpacity ?? 0.1} onChange={v => setStyle({ fillOpacity: v })} />}
      {allChannel && <Slider label="Mid" value={items[0].style.midOpacity ?? 1} onChange={v => setStyle({ midOpacity: v })} />}
      <span style={S.sep} />
      {allPriceLabels && (
        <button type="button" title={tagsOn ? 'Hide price labels' : 'Show price labels'}
                aria-label="Price labels" aria-pressed={tagsOn}
                style={{ ...S.btn, ...on(tagsOn) }} onClick={() => setStyle({ priceTag: !tagsOn })}>
          <IconTag size={16} />
        </button>
      )}
      {allMeasure && (
        <>
          {[
            ['measurePrice', 'Price change', <IconTag size={16} />],
            ['measurePercent', 'Percentage change', '%'],
            ['measureCandles', 'Candle count', <IconCandles size={16} />],
          ].map(([key, label, icon]) => {
            const enabled = items.every(d => d.style[key] !== false);
            return <button key={key} type="button" title={`${enabled ? 'Hide' : 'Show'} ${label}`}
                           aria-label={label} aria-pressed={enabled}
                           style={{ ...S.btn, padding: '0 6px', ...on(enabled) }}
                           onClick={() => setStyle({ [key]: !enabled })}>{icon}</button>;
          })}
          <span style={S.sep} />
        </>
      )}
      {(one?.type === 'text' || callout) && (
        <button title="Edit text" style={S.btn} onClick={() => onEditText(one)}><IconText size={16} /></button>
      )}
      {callout && (
        <>
          <span style={S.sep} />
          <label style={S.slider} title="Outline and pointer colour">Outline
            <input type="color" aria-label="Outline colour" value={colour(one.style.color, '#7c3aed')}
                   onChange={e => setStyle({ color: e.target.value })} />
          </label>
          <label style={S.slider} title="Text colour">Text
            <input type="color" aria-label="Text colour" value={colour(one.style.textColor || css('--text'), '#1a1a1a')}
                   onChange={e => setStyle({ textColor: e.target.value })} />
          </label>
          <label style={S.slider} title="Fill colour">Fill
            <input type="color" aria-label="Fill colour" value={colour(one.style.backgroundColor || css('--surface'), '#ffffff')}
                   onChange={e => setStyle({ backgroundColor: e.target.value })} />
          </label>
        </>
      )}
      {one?.type === 'alert' && (
        <button title="Alert settings" style={S.btn} onClick={() => onSettings(one)}>🔔</button>
      )}
      {one?.type === 'fib' && (
        <button title="Fib levels and shading" style={S.btn} onClick={() => onSettings(one)}><IconFib size={16} /></button>
      )}
      <button title={allLocked ? 'Unlock' : 'Lock'} style={{ ...S.btn, ...on(allLocked) }} onClick={() => onChange({ locked: !allLocked })}>
        <IconLock size={16} />
      </button>
      <button title={items.length > 1 ? `Delete ${items.length} drawings` : 'Delete'} style={{ ...S.btn, color: 'var(--error)' }} onClick={onDelete}><IconTrash size={16} /></button>
    </div>
  );
}
